import {
  isTokenExpirationError,
  useSetAuthenticationDialogCurrentScreenAtom,
  useSetCurrentCartId,
  useSetIsAuthenticationDialogAtom,
  useSetStytchTokenTypeAtom,
  useUserSessionSetAtom
} from "@RHCommerceDev/hooks-atoms";
import useIsoRedirect from "@RHCommerceDev/hooks-use-isoredirect";
import useGetCartProjectLazyQuery from "@RHCommerceDev/hooks/queries/useGetCartProjectLazyQuery";
import useAsync from "@RHCommerceDev/hooks/useAsync";
import memoize from "@RHCommerceDev/utils/memoize";
import { useMutation } from "@apollo/client";
import { useStytch } from "@stytch/react";
import { Products } from "@stytch/vanilla-js";
import { stytchSuccessMutation } from "graphql-client/queries/user";
import { useIsCartPages } from "@RHCommerceDev/hooks/useIsCartPages";
import { useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router";
import { unable_to_auth_magic_link } from "utils/constants";

const Auth = () => {
  const stytchClient = useStytch();
  const location = useLocation();
  const history = useHistory();
  const { isShoppingCartPage } = useIsCartPages();
  const setCurrentCartId = useSetCurrentCartId();
  const getSearchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const setAuthenticationDialogCurrentScreen =
    useSetAuthenticationDialogCurrentScreenAtom();
  const setTokenExpirationError = useSetAtom(isTokenExpirationError);
  const setStytchTokenTypeAtom = useSetStytchTokenTypeAtom();
  const setUserSessionAtom = useUserSessionSetAtom();
  const setOpenAuthenticationDialog = useSetIsAuthenticationDialogAtom();
  const [getCartProjection] = useGetCartProjectLazyQuery();
  const redirectUrl = getSearchParams.get("redirect_url");
  const isPasswordResetScreen =
    getSearchParams.get("stytch_token_type") === "reset_password";
  const isoRedirect = (url: string, type: string) =>
    useIsoRedirect(url, type, { history });

  const [stytchSuccess] = useMutation<Mutation>(stytchSuccessMutation, {
    context: {
      fetchOptions: {
        method: "POST"
      }
    },
    onCompleted: ({ stytchSuccess }) => {
      setUserSessionAtom(prevData => ({
        ...prevData,
        ...stytchSuccess,
        ...(isShoppingCartPage && { isCartMerge: true })
      }));
      setCurrentCartId(stytchSuccess?.currentCartId as string);
      getCartProjection({ id: stytchSuccess?.rhuid });
    },
    onError() {}
  });

  const config = {
    emailMagicLinksOptions: {
      loginExpirationMinutes: 30,
      loginRedirectURL: redirectUrl ?? history.location.pathname
    },
    products: [Products.emailMagicLinks, Products.passkeys]
  };

  const removeQueryParam = useCallback(
    (param: string) => {
      const params = getSearchParams;
      params.delete(param);
      history.replace({
        ...location,
        search: params.toString() ? `?${params.toString()}` : ""
      });
    },
    [getSearchParams, history]
  );

  useEffect(() => {
    // this effect will only run when  the type is present in the query params
    const type = getSearchParams?.get("type");

    if (Boolean(type)) {
      removeQueryParam("type");
    }
  }, [getSearchParams, removeQueryParam]);

  const handleType = useCallback(
    stytchUser => {
      const redirectType = getSearchParams?.get("redirect_url")?.split("=")[1];
      /*const url = new URL(config.emailMagicLinksOptions.loginRedirectURL);
      url.search = getSearchParams.toString();
      isoRedirect(url.href, "historyReplace");
      */
      isoRedirect(
        config.emailMagicLinksOptions.loginRedirectURL,
        "historyReplace"
      );

      if (redirectType === "signup") {
        setOpenAuthenticationDialog(true);
        setAuthenticationDialogCurrentScreen("Welcome");
        stytchSuccess();
      } else {
        setOpenAuthenticationDialog(false);
        stytchSuccess();
      }
    },
    [
      config.emailMagicLinksOptions.loginRedirectURL,
      getSearchParams,
      isoRedirect,
      setAuthenticationDialogCurrentScreen,
      setOpenAuthenticationDialog,
      stytchSuccess
    ]
  );

  const { run } = useAsync(async () => {
    const params = getSearchParams;
    const token = params.get("token");
    const stytchTokenType = params.get("stytch_token_type") ?? "";
    setStytchTokenTypeAtom(stytchTokenType);
    try {
      if (token) {
        const stytchUser = await stytchClient.magicLinks.authenticate(token, {
          session_duration_minutes: 129600
        });

        if (isPasswordResetScreen) {
          setOpenAuthenticationDialog(true);
          if (stytchUser?.user?.untrusted_metadata?.is2FA === true) {
            setOpenAuthenticationDialog(true);
            setAuthenticationDialogCurrentScreen("SmsCodeHandler");
          } else {
            setOpenAuthenticationDialog(true);
            setAuthenticationDialogCurrentScreen("ForgotPassword");
          }
        } else {
          if (stytchUser?.user?.untrusted_metadata?.is2FA === true) {
            setOpenAuthenticationDialog(true);
            setAuthenticationDialogCurrentScreen("SmsCodeHandler");
          } else {
            handleType(stytchUser);
          }
        }
      }
    } catch (error: any) {
      if (error.error_type === unable_to_auth_magic_link) {
        setOpenAuthenticationDialog(true);
        setTokenExpirationError(true);
        const type = getSearchParams.get("redirect_url")?.split("=")[1];
        if (type) {
          isoRedirect(
            config.emailMagicLinksOptions.loginRedirectURL,
            "historyReplace"
          );
        }
        if (type === "signup") {
          setAuthenticationDialogCurrentScreen("SignUpWithEmailAndPassword");
        } else if (type === "login") {
          setAuthenticationDialogCurrentScreen("SignInWithaSecureLink");
        }
      } else {
        console.log("error", error);
      }
    }
  });

  useEffect(() => {
    run();
  }, []);

  return null;
};

export default memoize(Auth);
